import { useAtomValue } from "@effect/atom-react";
import {
  fetchCodexSubscriptionAuthStatus,
  requestCodexSubscriptionAuthAction,
} from "@t3tools/client-runtime/providerConnections";
import { ManagedRelay } from "@t3tools/client-runtime/relay";
import type { CodexSubscriptionAuthAction, EnvironmentId } from "@t3tools/contracts";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback } from "react";

import { createRuntimeCommand } from "@t3tools/client-runtime/state/runtime";

import {
  presentCodexSubscriptionAuthStatus,
  type CodexSubscriptionConnection,
} from "../components/settings/CodexCloudSettings.logic";
import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { environmentSession } from "./session";
import { useAtomCommand } from "./use-atom-command";

class CodexSubscriptionAuthActionError extends Schema.TaggedErrorClass<CodexSubscriptionAuthActionError>()(
  "CodexSubscriptionAuthActionError",
  { detail: Schema.String },
) {
  override get message(): string {
    return this.detail;
  }
}

const EMPTY_CODEX_SUBSCRIPTION_STATUS_ATOM = Atom.make(
  AsyncResult.initial<never, never>(true),
).pipe(Atom.withLabel("codex-subscription-auth:empty"));

const codexSubscriptionStatusAtom = Atom.family((environmentId: EnvironmentId) =>
  connectionAtomRuntime
    .atom((get) => {
      const prepared = Option.getOrNull(
        get(environmentSession.preparedConnectionValueAtom(environmentId)),
      );
      if (prepared === null) {
        return Effect.never;
      }
      return Effect.gen(function* () {
        const signer = yield* Effect.serviceOption(ManagedRelay.ManagedRelayDpopSigner);
        return yield* fetchCodexSubscriptionAuthStatus({ prepared, signer });
      });
    })
    .pipe(
      Atom.swr({ staleTime: 15_000, revalidateOnMount: true }),
      Atom.setIdleTTL(5 * 60_000),
      Atom.withLabel(`codex-subscription-auth:${environmentId}`),
    ),
);

const codexSubscriptionAuthAction = createRuntimeCommand(connectionAtomRuntime, {
  label: "codex-subscription-auth:action",
  execute: (
    input: { readonly environmentId: EnvironmentId; readonly action: CodexSubscriptionAuthAction },
    registry,
  ) => {
    const prepared = Option.getOrNull(
      registry.get(environmentSession.preparedConnectionValueAtom(input.environmentId)),
    );
    if (prepared === null) {
      return Effect.die(
        new CodexSubscriptionAuthActionError({
          detail: "The environment terminal is not connected.",
        }),
      );
    }
    return Effect.gen(function* () {
      const signer = yield* Effect.serviceOption(ManagedRelay.ManagedRelayDpopSigner);
      return yield* requestCodexSubscriptionAuthAction({ prepared, action: input.action, signer });
    });
  },
});

export interface CodexSubscriptionConnectionView {
  readonly connection: CodexSubscriptionConnection;
  readonly refresh: () => void;
  readonly connect: () => Promise<void>;
  readonly disconnect: () => Promise<void>;
}

function actionFailureMessage(result: { readonly cause: Cause.Cause<unknown> }): string {
  const cause = Cause.squash(result.cause);
  return cause instanceof Error && cause.message.trim().length > 0
    ? cause.message
    : "Could not prepare the Codex subscription command.";
}

function copyCommand(command: string): Promise<void> {
  if (typeof navigator === "undefined" || navigator.clipboard?.writeText === undefined) {
    return Promise.reject(new Error("Clipboard access is unavailable in this browser."));
  }
  return navigator.clipboard.writeText(command);
}

export function useCodexSubscriptionConnection(
  environmentId: EnvironmentId,
  options: { readonly enabled?: boolean } = {},
): CodexSubscriptionConnectionView {
  const enabled = options.enabled ?? true;
  const atom = enabled
    ? codexSubscriptionStatusAtom(environmentId)
    : EMPTY_CODEX_SUBSCRIPTION_STATUS_ATOM;
  const result = useAtomValue(atom);
  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);
  const runAction = useAtomCommand(codexSubscriptionAuthAction, { reportFailure: false });
  const status = result === null ? null : Option.getOrNull(AsyncResult.value(result));

  const runTerminalAction = useCallback(
    async (action: Exclude<CodexSubscriptionAuthAction, "refresh">) => {
      if (!enabled) {
        throw new Error("This environment does not support Codex subscription authentication.");
      }
      const result = await runAction({ environmentId, action });
      if (result._tag === "Failure") {
        throw new Error(actionFailureMessage(result));
      }
      if (result.value.disposition !== "open-terminal") {
        throw new Error("The environment did not return a terminal command.");
      }
      await copyCommand(result.value.command);
    },
    [enabled, environmentId, runAction],
  );

  return {
    connection: !enabled
      ? {
          status: "error",
          message: "This environment does not support Codex subscription authentication.",
        }
      : result !== null && result._tag === "Failure"
        ? {
            status: "error",
            message: "Could not verify Codex subscription status in this environment.",
          }
        : status === null
          ? { status: "loading" }
          : presentCodexSubscriptionAuthStatus(status),
    refresh: enabled ? refresh : () => undefined,
    connect: useCallback(() => runTerminalAction("connect"), [runTerminalAction]),
    disconnect: useCallback(() => runTerminalAction("disconnect"), [runTerminalAction]),
  };
}
